import { useState, useMemo } from 'react';

import { Health, HealthServices, LocalStackEdition, LocalStackEvent, SERVICE_NAME } from '@localstack/types';

import { DEFAULT_RESOURCE_BASE_URL } from '@localstack/constants';

import { AwsClientOverrides } from '../aws';
import { usePolledData } from '../status/usePolledData';
/**
 * A little hook to automatically keep track of localstack health
 */
type LocalStackStatus = {
  /**
   * Whether the LocalStack instance is currently running
   */
  running: boolean;

  /**
   * Version of the running LocalStack container (e.g., "3.0.2")
   */
  version?: string;

  /**
   * Edition of the running LocalStack container
   */
  edition?: Health['edition'];

  /**
   * Available services in the LocalStack container and their health status
   */
  services?: HealthServices;

  /**
   * Whether this is a LocalStack Pro instance
   */
  isPro?: boolean;

  /**
   * Whether this is a Snowflake instance
   */
  isSnowflake?: boolean;

  /**
   * Loading indicator - `true` until the initial health check completes
   */
  isLoading: boolean;
};
export const useLocalstackStatus = (clientOverrides?: AwsClientOverrides, enable?: boolean): LocalStackStatus => {
  const healthEndpoint = `${clientOverrides?.endpoint ?? DEFAULT_RESOURCE_BASE_URL}/_localstack/health`;
  const { data, isLoading } = usePolledData<Health>({ endpoint: healthEndpoint, enable });
  const running = !!data;
  const servicesList = Object.keys(data?.services ?? {});
  const isPro = data?.edition !== LocalStackEdition.COMMUNITY;
  const isSnowflake = servicesList.includes(SERVICE_NAME.SNOWFLAKE);
  return { running, ...data, isPro, isSnowflake, isLoading };
};

type LocalStackEventsHandler = {
  setLogs: (logs: string) => void;
  logs?: LocalStackEvent[];
};

export const useLocalStackEvents = (): LocalStackEventsHandler => {
  const [events, setEvents] = useState<string>('');

  const parsedEvents: LocalStackEvent[] = useMemo(() => {
    const splittedEvents = events.split('\n');
    const nonEmptyEvents = splittedEvents.filter((line) => line !== '');
    return nonEmptyEvents.map((event) => JSON.parse(event));
  }, [events]);

  return {
    setLogs: setEvents,
    logs: parsedEvents,
  };
};
